import { BooleanInput, NumberInput } from '@angular/cdk/coercion';
import {
  booleanAttribute,
  Component,
  computed,
  input,
  model,
  numberAttribute,
  viewChild,
} from '@angular/core';
import { provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight } from '@ng-icons/lucide';

import {
  BrnCalendarCell,
  BrnCalendarCellButton,
  BrnCalendarGrid,
  BrnCalendarHeader,
  BrnCalendarNextButton,
  BrnCalendarPreviousButton,
  BrnCalendarRange,
  BrnCalendarWeek,
  BrnCalendarWeekday,
  injectBrnCalendarI18n,
  Weekday,
} from '@spartan-ng/brain/calendar';
import { injectDateAdapter } from '@spartan-ng/brain/date-time';
import { hlm } from '@spartan-ng/helm/utils';
import { buttonVariants } from '@spartan-ng/ui-button-helm';
import { HlmIconComponent } from '@spartan-ng/ui-icon-helm';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import type { ClassValue } from 'clsx';

/**
 * Range variant of HlmCalendarComponent, written against brain alpha.390.
 *
 * Structure, sizing and class strings deliberately mirror hlm-calendar.component.ts
 * so the two render as siblings. Notes on this brain version:
 *  - range attributes are `data-range-start` / `data-range-end` /
 *    `data-range-between` (upstream's newer template uses `data-range-middle`)
 *  - data attributes are emitted as empty strings, so selectors must test for
 *    presence (`data-[today]:`), never for a value (`data-[today=true]:`)
 *  - `brnCalendarMonthSelect` / `brnCalendarYearSelect` do not exist here, so
 *    there is no dropdown caption layout
 *  - `highlightDays` does not exist here
 */
@Component({
  standalone: true,
  selector: 'hlm-calendar-range',
  imports: [
    BrnCalendarRange,
    BrnCalendarHeader,
    BrnCalendarNextButton,
    BrnCalendarPreviousButton,
    BrnCalendarWeekday,
    BrnCalendarWeek,
    BrnCalendarCellButton,
    BrnCalendarCell,
    BrnCalendarGrid,
    HlmIconComponent,
    HlmInputDirective,
  ],
  viewProviders: [provideIcons({ lucideChevronLeft, lucideChevronRight })],
  template: `
    <div hlmInput userClass="h-100" [class]="calendarClass()">
      <div
        brnCalendarRange
        [min]="min()"
        [max]="max()"
        [disabled]="disabled()"
        [(startDate)]="startDate"
        [(endDate)]="endDate"
        [dateDisabled]="dateDisabled()"
        [weekStartsOn]="weekStartsOn()"
        [defaultFocusedDate]="defaultFocusedDate()"
        [class]="_computedCalenderClass()"
      >
        <div class="inline-flex flex-col space-y-4">
          <!-- Header -->
          <div class="space-y-4">
            <div class="relative flex items-center justify-center pt-1">
              <div brnCalendarHeader>
                {{ heading() }}
              </div>

              <div class="flex items-center space-x-1">
                <button
                  brnCalendarPreviousButton
                  class="ring-offset-background focus-visible:ring-ring border-input hover:bg-accent hover:text-accent-foreground absolute left-1 inline-flex h-7 w-7 items-center justify-center whitespace-nowrap rounded-md border bg-transparent p-0 text-sm font-medium opacity-50 transition-colors hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  <hlm-icon hlm name="lucideChevronLeft" size="lg" />
                </button>

                <button
                  brnCalendarNextButton
                  class="ring-offset-background focus-visible:ring-ring border-input hover:bg-accent hover:text-accent-foreground absolute right-1 inline-flex h-7 w-7 items-center justify-center whitespace-nowrap rounded-md border bg-transparent p-0 text-sm font-medium opacity-50 transition-colors hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
                >
                  <hlm-icon hlm name="lucideChevronRight" size="lg" />
                </button>
              </div>
            </div>
          </div>

          <table class="w-full border-collapse space-y-1" brnCalendarGrid>
            <thead class="border-none">
              <tr class="flex">
                <th
                  *brnCalendarWeekday="let weekday"
                  scope="col"
                  class="text-muted-foreground w-[5rem] rounded-md flex items-center justify-center"
                  [attr.aria-label]="i18n.config().labelWeekday(weekday)"
                >
                  {{ i18n.config().formatWeekdayName(weekday) }}
                </th>
              </tr>
            </thead>

            <tbody role="rowgroup">
              <tr *brnCalendarWeek="let week" class="mt-2 flex w-full">
                @for (date of week; track dateAdapter.getTime(date)) {
                  <td brnCalendarCell [class]="cellClass(date)">
                    <button brnCalendarCellButton [date]="date" [class]="btnClass">
                      {{ dateAdapter.getDate(date) }}
                    </button>
                  </td>
                }
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `,
  styles: `
    [data-selected] {
      background-color: var(--color-active) !important;
      color: var(--color-surface);
      -webkit-text-fill-color: inherit;
    }
  `,
})
export class HlmCalendarRangeComponent<T> {
  public readonly calendarClass = input<ClassValue>('');

  protected readonly _computedCalenderClass = computed(() => hlm('py-3 h-auto', this.calendarClass()));

  /** Access the calendar i18n */
  protected readonly i18n = injectBrnCalendarI18n();

  /** Access the date time adapter */
  protected readonly dateAdapter = injectDateAdapter<T>();

  /** The minimum date that can be selected.*/
  public readonly min = input<T>();

  /** The maximum date that can be selected. */
  public readonly max = input<T>();

  /** Determine if the date picker is disabled. */
  public readonly disabled = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  /** The start of the selected range. */
  public readonly startDate = model<T | undefined>();

  /** The end of the selected range. */
  public readonly endDate = model<T | undefined>();

  /** Whether a specific date is disabled. */
  public readonly dateDisabled = input<(date: T) => boolean>(() => false);

  /** The day the week starts on */
  public readonly weekStartsOn = input<Weekday, NumberInput>(0, {
    transform: (v: unknown) => numberAttribute(v) as Weekday,
  });

  /** The default focused date. */
  public readonly defaultFocusedDate = input<T>();

  /**
   * Access the calendar directive. Non-required so template expressions that run
   * before the query resolves fall back gracefully rather than throwing.
   */
  private readonly _calendar = viewChild(BrnCalendarRange<T>);

  /** Get the heading for the current month and year */
  protected heading = computed(() => {
    const calendar = this._calendar();
    if (!calendar) return '';

    const focused = calendar.focusedDate();
    return this.i18n
      .config()
      .formatHeader(this.dateAdapter.getMonth(focused), this.dateAdapter.getYear(focused));
  });

  /**
   * The connecting band is painted on the cell so it spans the full width with
   * no gaps between days. Bound as a method rather than a `has-[...]` variant to
   * stay within Tailwind v3.
   */
  protected cellClass(date: T): string {
    const calendar = this._calendar();
    const complete = !!this.startDate() && !!this.endDate();

    const isStart = !!complete && !!calendar?.isStartOfRange(date);
    const isEnd = !!complete && !!calendar?.isEndOfRange(date);
    const isBetween = !!complete && !!calendar?.isBetweenRange(date);

    return hlm(
      'data-[selected]:data-[outside]:bg-accent/50 data-[selected]:bg-accent relative h-[4rem] w-[5rem] p-0 text-center focus-within:relative focus-within:z-20 first:data-[selected]:rounded-l-md last:data-[selected]:rounded-r-md',
      isBetween ? 'bg-accent' : '',
      isStart ? 'bg-accent rounded-l-md' : '',
      isEnd ? 'bg-accent rounded-r-md' : '',
    );
  }

  protected readonly btnClass = hlm(
    buttonVariants({ variant: 'ghost' }),
    'h-[4rem] w-[5rem] font-size-inherit p-0 aria-selected:opacity-100',
    'data-[outside]:text-muted-foreground bg-transparent data-[outside]:opacity-50 data-[outside]:aria-selected:bg-accent/50 data-[outside]:aria-selected:text-muted-foreground data-[outside]:aria-selected:opacity-30',
    'data-[today]:bg-accent data-[today]:text-accent-foreground',
    'data-[selected]:bg-primary data-[selected]:text-primary-foreground data-[selected]:hover:bg-primary data-[selected]:hover:text-primary-foreground data-[selected]:focus:bg-primary data-[selected]:focus:text-primary-foreground',
    'data-[disabled]:text-muted-foreground data-[disabled]:opacity-50',
    // range-specific: endpoints are already covered by data-[selected] above,
    // so this only handles corner rounding and the interior days.
    'data-[range-start]:rounded-l-md data-[range-start]:rounded-r-none',
    'data-[range-end]:rounded-r-md data-[range-end]:rounded-l-none',
    'data-[range-between]:rounded-none data-[range-between]:bg-transparent data-[range-between]:text-accent-foreground',
  );
}
